type Template = { id: number; name: string }

type MatchResult = {
  /** 一致したひな形の id（重複排除済み） */
  matchedIds: number[]
  /** どのひな形にも一致しなかったトークン（元の表記・重複排除済み） */
  unmatchedNames: string[]
}

/** NFKC 正規化 + trim。突合のキーを揃えるために使う */
const normalizeName = (value: string): string => value.normalize('NFKC').trim()

/**
 * 改行を含むか。
 *
 * 一括選択として扱う(=貼り付けを乗っ取る)かの判定に使う。改行を含まない
 * 単一語の貼り付けは通常の検索絞り込みに任せ、乗っ取らない。
 * 区切りは改行のみ(カンマは区切りにしない)。ひな形名にカンマが含まれうるため。
 */
export const containsTemplateSeparator = (text: string): boolean =>
  /[\r\n]/.test(text)

/**
 * 貼り付けテキストをひな形名のトークン列に分割する。
 *
 * - 改行(CRLF / CR / LF)でのみ分割する。CRLF を二重分割しないよう \r\n を先頭に置く。
 *   カンマは区切りにしない(ひな形名にカンマが含まれうるため)。
 * - 各トークンは trim し、空文字は捨てる。
 */
export const parsePastedTemplateNames = (text: string): string[] =>
  text
    .split(/\r\n|\r|\n/)
    .map(token => token.trim())
    .filter(token => token.length > 0)

/**
 * トークン列をひな形名と完全一致で突合する。
 *
 * - トークン・ひな形名の双方を NFKC 正規化 + trim してから突合する（大文字小文字はそのまま）。
 * - 1 トークンが複数ひな形に一致した場合（同名ひな形）は一致した全件を対象にする。
 * - 一致した id・未マッチ名はそれぞれ重複排除する。
 */
export const matchPastedTemplates = (
  tokens: string[],
  templates: Template[],
): MatchResult => {
  const matchedIds: number[] = []
  const seenIds = new Set<number>()
  const unmatchedNames: string[] = []
  const seenUnmatched = new Set<string>()

  for (const token of tokens) {
    const normalizedToken = normalizeName(token)
    const matches = templates.filter(
      template => normalizeName(template.name) === normalizedToken,
    )

    if (matches.length === 0) {
      if (!seenUnmatched.has(token)) {
        seenUnmatched.add(token)
        unmatchedNames.push(token)
      }
      continue
    }

    for (const match of matches) {
      if (!seenIds.has(match.id)) {
        seenIds.add(match.id)
        matchedIds.push(match.id)
      }
    }
  }

  return { matchedIds, unmatchedNames }
}

/**
 * 既存の選択を保持しつつ、一致した id を重複なく追記する（union）。
 */
export const unionTemplateIds = (
  existingIds: number[],
  idsToAdd: number[],
): number[] => Array.from(new Set([...existingIds, ...idsToAdd]))

type PasteResultMessage = {
  severity: 'success' | 'info'
  text: string
}

/**
 * 貼り付け一括選択の結果を 1 つの通知メッセージにまとめる。
 *
 * スナックバー基盤は同時に 1 件しか表示できない（後勝ちで上書きされる）ため、
 * 追加件数と未マッチを 1 メッセージに統合して 1 回だけ表示する。
 *
 * - 追加あり: success。未マッチがあれば同じ文面に「（該当なし: …）」を付す。
 * - 追加なし・未マッチあり: info で未マッチのみ通知。
 * - 追加なし・未マッチなし（= 一致したが全て選択済み）: info で「すでに全て選択済み」を通知し、
 *   無反応にならないようにする。
 *
 * 貼り付け乗っ取りの前提（トークンが 1 件以上ある）下では常にいずれかに該当するため、
 * 戻り値は常に定義される。
 */
export const buildPasteResultMessage = (
  addedCount: number,
  unmatchedNames: string[],
): PasteResultMessage => {
  const unmatchedSuffix =
    unmatchedNames.length > 0
      ? `（該当なし: ${unmatchedNames.join('、')}）`
      : ''

  if (addedCount > 0) {
    return {
      severity: 'success',
      text: `${addedCount}件を選択に追加しました${unmatchedSuffix}`,
    }
  }

  if (unmatchedNames.length > 0) {
    return {
      severity: 'info',
      text: `該当なし: ${unmatchedNames.join('、')}`,
    }
  }

  return { severity: 'info', text: 'すでに全て選択済みです' }
}
